import { User, UserCreatePayload, UserUpdatePayload } from '@ekkogmbh/apisdk';
import { Add, PersonOutline, Save, SmartToyOutlined } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import SectionDrawerContent from 'src/SettingsSection/Components/SectionDrawerContent';

interface Props {
  user?: User;
  closeHandler: () => void;
  saveHandler: (user: UserUpdatePayload | UserCreatePayload, password?: string) => Promise<void>;
  passwordEditable?: boolean;
}

const SectionHeader = ({ title }: { title: string }): React.JSX.Element => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: 'text.secondary',
      fontSize: 11,
    }}
  >
    {title}
  </Typography>
);

const defaultValidUntil = (): string => moment().add(1, 'd').format('YYYY-MM-DD');

export const UsersAdminDrawer = ({ user, saveHandler, closeHandler, passwordEditable }: Props): React.JSX.Element => {
  const isEdit = user !== undefined;

  const [username, setUsername] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [isAutomation, setIsAutomation] = useState<boolean>(false);
  const [validUntil, setValidUntil] = useState<string>(
    user?.validUntil ? moment(user.validUntil).format('YYYY-MM-DD') : defaultValidUntil(),
  );
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');

  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const touch = (field: string): void => setTouchedFields((prev) => ({ ...prev, [field]: true }));

  const reset = useCallback(() => {
    setUsername(user?.username ?? '');
    setEmail(user?.email ?? '');
    setCoordinate(user?.coordinate ?? '');
    setIsAutomation(!!user?.automation);
    setValidUntil(user?.validUntil ? moment(user.validUntil).format('YYYY-MM-DD') : defaultValidUntil());
    setPassword('');
    setPasswordConfirm('');
    setTouchedFields({});
  }, [user]);

  useEffect(() => {
    reset();
  }, [reset]);

  // Password is required when CREATING and the auth mechanism supports
  // local passwords. When EDITING, password is optional — empty means
  // "don't change", any text means the user wants a new one.
  const isPasswordOk = useMemo(() => {
    if (!passwordEditable) {
      return true;
    }
    if (!isEdit) {
      return password !== '' && password === passwordConfirm;
    }
    if (password === '' && passwordConfirm === '') {
      return true;
    }
    return password !== '' && password === passwordConfirm;
  }, [passwordEditable, isEdit, password, passwordConfirm]);

  const isValid = useMemo(
    () => username !== '' && email !== '' && coordinate !== '' && (isAutomation || validUntil !== '') && isPasswordOk,
    [username, email, coordinate, isAutomation, validUntil, isPasswordOk],
  );

  const onSave = async (): Promise<void> => {
    if (isEdit && user) {
      const payload: UserUpdatePayload = {
        id: user.id,
        email,
        validUntil,
        coordinate,
      };
      await saveHandler(payload, password !== '' ? password : undefined);
    } else {
      const payload: UserCreatePayload = {
        username,
        email,
        coordinate,
        automation: isAutomation,
        validUntil: isAutomation ? undefined : validUntil,
      };
      await saveHandler(payload, password !== '' ? password : undefined);
    }
  };

  return (
    <SectionDrawerContent
      onClose={closeHandler}
      header={
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {isEdit && isAutomation ? <SmartToyOutlined color="action" /> : <PersonOutline color="action" />}
          <Stack spacing={0}>
            <Typography variant="h6" fontWeight={700}>
              {isEdit ? 'Edit user' : 'Add user'}
            </Typography>
            {isEdit && (
              <Typography variant="caption" color="text.secondary">
                {user?.username}
              </Typography>
            )}
          </Stack>
        </Stack>
      }
      actions={{
        reset: { onClick: reset },
        confirm: {
          label: isEdit ? 'Save' : 'Create',
          icon: isEdit ? <Save /> : <Add />,
          onClick: onSave,
          disabled: !isValid,
        },
      }}
    >
      <Stack spacing={4}>
        {/* Account */}
        <Stack spacing={1.5}>
          <SectionHeader title="Account" />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            // Force equal-width columns. Stack doesn't flex its children
            // by default — each TextField would size to its content
            // (Username with its action button is wider than Email
            // alone), so we hand each cell flex:1 explicitly. minWidth:0
            // lets the inputs shrink below their natural width on narrow
            // viewports instead of overflowing.
            sx={{ '& > *': { flex: 1, minWidth: 0 } }}
          >
            <StyledTextField
              label="Username *"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onBlur={() => touch('username')}
              error={touchedFields.username && username === ''}
              disabled={isEdit}
              action={{
                disabled: isEdit,
                icon: isAutomation ? SmartToyOutlined : PersonOutline,
                onClick: () => setIsAutomation((v) => !v),
                tooltip: isAutomation ? 'automation-user' : 'user',
              }}
            />
            <StyledTextField
              label="E-mail *"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => touch('email')}
              error={touchedFields.email && email === ''}
            />
          </Stack>
        </Stack>

        {/* Location */}
        <Stack spacing={1.5}>
          <SectionHeader title="Location" />
          <CoordinateInput value={coordinate} onChange={setCoordinate} trailingDelimiter={false} />
        </Stack>

        {/* Security */}
        {passwordEditable && (
          <Stack spacing={1.5}>
            <SectionHeader title="Security" />
            {isEdit && (
              <Typography variant="caption" color="text.secondary">
                Leave empty to keep the current password.
              </Typography>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ '& > *': { flex: 1, minWidth: 0 } }}>
              <StyledTextField
                label={isEdit ? 'Password' : 'Password *'}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => touch('password')}
                error={touchedFields.password && !isPasswordOk}
                inputProps={{ autoComplete: 'new-password' }}
              />
              <StyledTextField
                label={isEdit ? 'Confirm password' : 'Confirm password *'}
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                onBlur={() => touch('passwordConfirm')}
                error={touchedFields.passwordConfirm && passwordConfirm !== '' && password !== passwordConfirm}
                helperText={
                  touchedFields.passwordConfirm && passwordConfirm !== '' && password !== passwordConfirm
                    ? 'Passwords do not match'
                    : undefined
                }
                inputProps={{ autoComplete: 'new-password' }}
              />
            </Stack>
          </Stack>
        )}

        {/* Validity */}
        {!isAutomation && (
          <Stack spacing={1.5}>
            <SectionHeader title="Validity" />
            <DatePicker
              label="Valid until"
              disablePast
              value={moment(validUntil)}
              onChange={(date: Moment | null) => {
                if (date !== null) {
                  setValidUntil(date.format('YYYY-MM-DD'));
                }
              }}
              minDate={moment().add(1, 'd')}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Stack>
        )}
      </Stack>
    </SectionDrawerContent>
  );
};
